import React, { useEffect, useState } from "react";
import styles from "./Auth.module.css";
import InputField from "./InputField";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import aws_exports from "../../aws-exports";
import AuthErrorBoundary from "../../components/AuthErrorBoundary";

Amplify.configure(aws_exports);

function Login() {
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    });
    const [eye, setEye] = useState(false); // For visibility
    // Initialize MFA state from localStorage for persistence across refreshes
    const [showMfaChallenge, setShowMfaChallenge] = useState(() => {
        return localStorage.getItem('mfa_challenge_active') === 'true';
    });
    const [mfaCode, setMfaCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [signInResult, setSignInResult] = useState(null); // Store Amplify signIn result with nextStep

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const source = queryParams.get('source');

    useEffect(() => {
        if(source === "registered") {
            toast.info("Account Created. Please, check your email for next steps.");
        }
    }, [source]);

    // Persist MFA challenge state across page refreshes
    useEffect(() => {
        if (showMfaChallenge) {
            localStorage.setItem('mfa_challenge_active', 'true');
        } else {
            localStorage.removeItem('mfa_challenge_active');
        }
    }, [showMfaChallenge]);

    function handleInputChange(field, value) {
        setFormData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    function eyeClick() {
        setEye((prev) => !prev)
    }

    // Helper function to handle successful authentication
    function handleSuccessfulAuth() {
        localStorage.removeItem('mfa_challenge_active');
        navigate("/portal");
    }

    // Helper function to get loading message based on Amplify's nextStep
    function getLoadingMessage() {
        if (!isSubmitting) return 'Login';
        
        if (!signInResult) return 'Signing in...';
        
        if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
            return showMfaChallenge ? 'Verifying code...' : 'MFA required...';
        }
        
        return 'Loading...';
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setIsSubmitting(true);

        const { email, password } = formData;

        if (!email || !password) {
            toast.error("Please fill out all fields.");
            setIsSubmitting(false);
            return;
        }

        try {
            const result = await signIn({username: email, password});
            setSignInResult(result);

            if (result.isSignedIn) {
                // User signed in successfully without MFA
                handleSuccessfulAuth();
            } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                // MFA challenge required
                setShowMfaChallenge(true);
                toast.info("Please enter your 6-digit TOTP code from your authenticator app.");
            } else {
                // Handle other possible next steps
                toast.error("Unexpected sign-in step required.");
            }
        } catch (error) {
            console.error("Sign in error: ", error);
            toast.error(error.message || "An error occurred during sign in.");
        } finally {
            setIsSubmitting(false);
        }
    }

    async function handleMfaSubmit(e) {
        e.preventDefault();
        
        if (!mfaCode || mfaCode.length !== 6) {
            toast.error("Please enter a valid 6-digit code.");
            return;
        }

        setIsSubmitting(true);

        try {
            const confirmResult = await confirmSignIn({
                challengeResponse: mfaCode
            });

            if (confirmResult.isSignedIn) {
                handleSuccessfulAuth();
            } else {
                toast.error("Invalid code. Please try again.");
            }
        } catch (error) {
            console.error("MFA confirmation error: ", error);
            toast.error(error.message || "Invalid code. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    function renderResetPassword() {
        navigate("/auth/forgot-password");
    }

    return (
        <AuthErrorBoundary>
            <div className={styles["login-content"]}>
                <ToastContainer
                    position="top-center"
                    autoClose={false}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeButton={true}
                />

            <div className={styles["auth-form-header"]}>
                <h5>{showMfaChallenge ? 'Enter Authentication Code' : 'Log Into Account'}</h5>

                {!showMfaChallenge && (
                    <p className={styles["switch-auth"]}>
                        Don't have an account? <a href="/auth/register" className={styles["txt-link"]}>Register</a> 
                    </p>
                )}

                {showMfaChallenge && (
                    <p className={styles["switch-auth"]}>
                        Enter the 6-digit code from your authenticator app
                    </p>
                )}
            </div>

            {!showMfaChallenge ? (
                <form className={styles["register-form"]} onSubmit={handleSubmit}>
                    <InputField 
                        desc="Email address*"
                        type="email"
                        name="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(value) => handleInputChange("email", value)}
                    />

                    <div className={`${styles["input"]} ${styles["password-input"]}`}>
                        <p className={styles["input-desc"]}>Password*</p>

                        <div className={styles["pwd"]}>
                            <input 
                                type={eye? "text" : "password"}
                                className={styles["input-field"]}
                                name="password"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) => handleInputChange("password", e.target.value)}
                            />

                            {eye?
                                <VisibilityIcon 
                                    className={styles["viz-icon"]} 
                                    onClick={eyeClick}
                                />
                                :
                                <VisibilityOffIcon 
                                    className={styles["viz-icon"]} 
                                    onClick={eyeClick}
                                />
                            }
                        </div>
                    </div>

                    <div className={styles["auth-form-footer"]}>
                        <div className={styles["auth-footer-text-wrapper"]}>
                            <p className={styles["auth-form-footer-txt"]}>
                                By submitting this form, you agree to the <a href="/tos" className={styles["txt-link"]}>terms of service</a> 
                            </p>

                            <p className={styles["auth-form-footer-txt"]}>
                                Forgot your password? <span className={styles["txt-link"]} onClick={renderResetPassword}>Reset password</span> 
                            </p>
                        </div>
                        
                        <Button 
                            className={`${styles["btn-dark"]}`} 
                            type="submit"
                        >
                            Submit
                            <ArrowForwardIcon className={styles["btn-arrow"]}/>
                        </Button>
                    </div>         
                </form>
            ) : (
                <form className={styles["register-form"]} onSubmit={handleMfaSubmit}>
                    <div className={styles["input"]}>
                        <p className={styles["input-desc"]}>Authentication Code*</p>
                        <input 
                            type="text"
                            className={styles["input-field"]}
                            name="mfaCode"
                            placeholder="000000"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            style={{
                                textAlign: 'center',
                                letterSpacing: '0.5rem',
                                fontSize: '1.2rem',
                                fontFamily: 'monospace'
                            }}
                            maxLength={6}
                            autoComplete="one-time-code"
                        />
                    </div>

                    <div className={styles["auth-form-footer"]}>
                        <div className={styles["auth-footer-text-wrapper"]}>
                            <p className={styles["auth-form-footer-txt"]}>
                                Open your authenticator app and enter the 6-digit code
                            </p>

                            <p className={styles["auth-form-footer-txt"]}>
                                <span className={styles["txt-link"]} onClick={() => {
                                    setShowMfaChallenge(false);
                                    setMfaCode('');
                                    setSignInResult(null);
                                }}>Back to login</span> 
                            </p>
                        </div>
                        
                        <Button 
                            className={`${styles["btn-dark"]}`} 
                            type="submit"
                            disabled={isSubmitting || mfaCode.length !== 6}
                        >
                            Verify Code
                            <ArrowForwardIcon className={styles["btn-arrow"]}/>
                        </Button>
                    </div>         
                </form>
            )}
            </div>
        </AuthErrorBoundary>
    )
}

export default Login;